import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { UserAdminInfo } from "../../api/users";
import { revenueCatCustomerUrl } from "../../api/constants";
import { useUsersStore } from "../../store/usersStore";
import { useDialogState } from "../../hooks/useDialogState";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import DelayedInput from "../common/DelayedInput";
import { UserActionsMenu } from "./user_actions/UserActionsMenu";
import { UserDetailDialog } from "./UserDetailDialog";

const RC_STORE_OPTIONS = [
  { value: "", label: "All providers" },
  { value: "app_store", label: "App Store (iOS)" },
  { value: "play_store", label: "Play Store (Android)" },
  { value: "stripe", label: "Stripe (Web)" },
  { value: "promotional", label: "Promotional" },
];

const RC_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "intro", label: "Intro" },
  { value: "grace_period", label: "Grace Period" },
  { value: "expired", label: "Expired" },
  { value: "no_subscription", label: "No subscription" },
];

export const UserList = () => {
  const { users, nextToken, loading, loadUsers, loadNextPage } = useUsersStore();
  const [emailFilter, setEmailFilter] = React.useState<string>();
  const [rcStoreFilter, setRcStoreFilter] = React.useState<string>("");
  const [rcStatusFilter, setRcStatusFilter] = React.useState<string>("");
  const [rcIdFilter, setRcIdFilter] = React.useState<string>("");
  const detailDialog = useDialogState<UserAdminInfo>();

  React.useEffect(() => {
    loadUsers(emailFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { id: "email", label: "Email" },
    { id: "displayName", label: "Name" },
    {
      id: "accountState",
      label: "State",
      render: (user: UserAdminInfo) => (
        <Typography
          sx={{
            color: user.accountState === "CONFIRMED" ? "green" : "red",
          }}
        >
          {user.accountState}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      render: (user: UserAdminInfo) => {
        const rcUrl = user.revenueCatId ? revenueCatCustomerUrl(user.revenueCatId) : undefined;
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="View details">
              <IconButton size="small" onClick={() => detailDialog.openWith(user)} sx={{ color: "#1976d2" }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {rcUrl && (
              <Tooltip title="Open in RevenueCat">
                <IconButton
                  size="small"
                  href={rcUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  component="a"
                  sx={{ color: "#e87722" }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <UserActionsMenu user={user} refreshUsers={refreshList} />
          </Box>
        );
      },
    },
  ];

  const refreshList = () => {
    loadUsers(emailFilter, rcStoreFilter || undefined, rcStatusFilter || undefined, rcIdFilter);
  };

  const handleLoadNext = () => {
    if (nextToken) {
      loadNextPage();
    }
  };

  const handleFilterChange = React.useCallback(
    (filt: string) => {
      setEmailFilter(filt);
      loadUsers(filt, rcStoreFilter || undefined, rcStatusFilter || undefined, rcIdFilter);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rcStoreFilter, rcStatusFilter, rcIdFilter]
  );

  const handleRcIdChange = React.useCallback(
    (id: string) => {
      setRcIdFilter(id);
      loadUsers(emailFilter, rcStoreFilter || undefined, rcStatusFilter || undefined, id || undefined);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [emailFilter, rcStoreFilter, rcStatusFilter]
  );

  const handleRcStoreChange = (store: string) => {
    setRcStoreFilter(store);
    loadUsers(emailFilter, store || undefined, rcStatusFilter || undefined, rcIdFilter);
  };

  const handleRcStatusChange = (status: string) => {
    setRcStatusFilter(status);
    loadUsers(emailFilter, rcStoreFilter || undefined, status || undefined, rcIdFilter);
  };

  const hasActiveFilters = !!emailFilter || !!rcStoreFilter || !!rcStatusFilter || !!rcIdFilter;

  const handleClearFilters = () => {
    setEmailFilter(undefined);
    setRcStoreFilter("");
    setRcStatusFilter("");
    setRcIdFilter("");
    loadUsers(undefined, undefined, undefined, undefined);
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (a.email < b.email) return -1;
    if (a.email > b.email) return 1;
    return 0;
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <DelayedInput
          label="Email starts with:"
          variant="outlined"
          value={emailFilter}
          onDelayedChange={(txt) => {
            if (txt !== emailFilter) {
              handleFilterChange(txt);
            }
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <DelayedInput
          label="RevenueCat ID:"
          variant="outlined"
          value={rcIdFilter}
          delay={100}
          onDelayedChange={(txt) => {
            if (txt !== rcIdFilter) {
              handleRcIdChange(txt);
            }
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          select
          label="Provider"
          value={rcStoreFilter}
          onChange={(e) => handleRcStoreChange(e.target.value)}
          size="small"
          sx={{ minWidth: 180 }}
        >
          {RC_STORE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={rcStatusFilter}
          onChange={(e) => handleRcStatusChange(e.target.value)}
          size="small"
          sx={{ minWidth: 160 }}
        >
          {RC_STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        {hasActiveFilters && (
          <Button variant="text" size="small" onClick={handleClearFilters} sx={{ whiteSpace: "nowrap" }}>
            Clear filters
          </Button>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={refreshList} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <InfinitePaginationTable
        columns={columns}
        items={sortedUsers}
        pageSize={10}
        loading={loading}
        onLoadNext={handleLoadNext}
      />
      <UserDetailDialog
        open={detailDialog.open}
        user={detailDialog.data}
        onClose={detailDialog.close}
      />
    </Box>
  );
};
